import "zod/compile";
import { z } from "zod";
import { reportNumberValue } from "./reportAggregate";

/**
 * How a Report shows a Number: in a KPI, a Table's column, or a chart's axis,
 * labels and tooltips. As in a spreadsheet's Format Cells: Number, Currency
 * or Percent (a fraction: 0.25 shows as 25%), with fixed decimals or as many
 * as the value needs, and optionally compact (1.2K, 3.4M).
 */
export const reportNumberFormatSchema = z.object({
  style: z.enum(["number", "currency", "percent"]).default("number"),
  /** Digits after the decimal point; absent = up to 2 (cents for Currency, 1 for Percent and compact). */
  decimals: z.number().int().min(0).max(6).optional(),
  /** ISO 4217 code, for `currency`. */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .default("USD"),
  compact: z.boolean().default(false),
});
export type ReportNumberFormat = z.infer<typeof reportNumberFormatSchema>;
export type ReportNumberFormatInput = z.input<typeof reportNumberFormatSchema>;

const formatters = new Map<string, Intl.NumberFormat>();

/** Fraction digits when a format sets none: cents for money, one for compact and percent. */
function autoDigits(format: ReportNumberFormat): [number, number] {
  if (format.compact) return [0, 1];
  if (format.style === "currency") return [2, 2];
  if (format.style === "percent") return [0, 1];
  return [0, 2];
}

/**
 * A Number format as `Intl.NumberFormat` options: what a chart is given to
 * write its numbers exactly as {@link formatReportNumber} does.
 */
export function reportNumberFormatOptions(
  input: ReportNumberFormatInput | undefined,
): Intl.NumberFormatOptions {
  const format = reportNumberFormatSchema.parse(input ?? {});
  const [min, max] =
    format.decimals === undefined
      ? autoDigits(format)
      : [format.decimals, format.decimals];
  return {
    style: format.style === "number" ? "decimal" : format.style,
    ...(format.style === "currency" && { currency: format.currency }),
    ...(format.compact && { notation: "compact" }),
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  };
}

/** One Intl formatter per format and locale: a Table formats thousands of cells. */
function formatter(
  input: ReportNumberFormatInput | undefined,
  locale: string | undefined,
) {
  const key = JSON.stringify([locale ?? "", input ?? {}]);
  const cached = formatters.get(key);
  if (cached) return cached;
  const created = new Intl.NumberFormat(
    locale,
    reportNumberFormatOptions(input),
  );
  formatters.set(key, created);
  return created;
}

/**
 * A Number as `format` shows it; a value that isn't a Number (null, text) is
 * shown as it is, and Empty as "".
 */
export function formatReportNumber(
  value: number | string | boolean | null | undefined,
  format: ReportNumberFormatInput | undefined,
  locale?: string,
): string {
  if (value === null || value === undefined) return "";
  const number = reportNumberValue(value);
  if (number === null) return String(value);
  return formatter(format, locale).format(number);
}
